import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CreditCard, Plus, RotateCw, ShieldOff, Repeat, X, Loader2, Eye, Check, Copy } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card as UICard, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

interface CardRow {
  id: string;
  nickname: string;
  bank: string;
  lastFour: string;
  cardholderName: string;
  ownerUserId: string | null;
  otpDeviceId: string | null;
  otpSimSlot: number | null;
  fxMarkupPct: string | null;
  fxGstPct: string | null;
  active: boolean;
  closedAt: string | null;
  closedReason: string | null;
  closedNotes: string | null;
  replacedByCardId: string | null;
  notes: string | null;
  cardProduct: string | null;
  hasSecureDetails?: boolean;
  creditLimit: string | null;
  dueDay: number | null;
}

type CardCcSummary = {
  id: string;
  used: string;
  available: string | null;
  daysToDue: number | null;
  hasBeneficiary: boolean;
  lastPaymentAt: string | null;
  lastPaymentAmount: string | null;
};

interface UserRow {
  id: string;
  name: string;
  email: string;
  role: string;
}

interface BankAccountRow {
  id: string;
  nickname: string;
  bank: string;
  accountNumberMasked: string | null;
  ifsc: string | null;
  active: boolean;
  notes: string | null;
}

const CLOSE_REASONS: Array<{ v: string; label: string }> = [
  { v: 'CLOSED_BY_USER', label: 'Closed by us' },
  { v: 'CLOSED_BY_BANK', label: 'Closed by bank' },
  { v: 'LOST', label: 'Lost' },
  { v: 'STOLEN', label: 'Stolen' },
  { v: 'FRAUD', label: 'Fraud' },
  { v: 'EXPIRED', label: 'Expired' },
  { v: 'OTHER', label: 'Other' },
];

export function CardsPage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';
  const [showClosed, setShowClosed] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [closing, setClosing] = useState<CardRow | null>(null);
  const [replacing, setReplacing] = useState<CardRow | null>(null);
  const [showAddBank, setShowAddBank] = useState(false);
  const [closingBank, setClosingBank] = useState<BankAccountRow | null>(null);
  const [secretsCard, setSecretsCard] = useState<CardRow | null>(null);
  const [ccSettingsCard, setCcSettingsCard] = useState<CardRow | null>(null);

  const cards = useQuery<CardRow[]>({
    queryKey: ['cards-admin', { showClosed }],
    queryFn: () =>
      api(`/api/cards?includeClosed=${showClosed ? 'true' : 'false'}`),
  });
  const users = useQuery<UserRow[]>({
    queryKey: ['users-list'],
    queryFn: () => api('/api/users'),
    enabled: isAdmin,
  });
  const bankAccounts = useQuery<BankAccountRow[]>({
    queryKey: ['bank-accounts-list'],
    queryFn: () => api('/api/bank-accounts'),
  });
  const ccSummaries = useQuery<CardCcSummary[]>({
    queryKey: ['cards', 'credit-summaries'],
    queryFn: () => api('/api/cards/credit-summaries'),
  });
  const ccByCardId = useMemo(() => {
    const m = new Map<string, CardCcSummary>();
    ccSummaries.data?.forEach((s) => m.set(s.id, s));
    return m;
  }, [ccSummaries.data]);

  const replacementById = useMemo(() => {
    const m = new Map<string, CardRow>();
    cards.data?.forEach((c) => m.set(c.id, c));
    return m;
  }, [cards.data]);
  const userNameById = useMemo(() => {
    const m = new Map<string, string>();
    users.data?.forEach((u) => m.set(u.id, u.name));
    return m;
  }, [users.data]);

  const grouped = useMemo(() => {
    const list = cards.data ?? [];
    const active = list.filter((c) => !c.closedAt);
    const closed = list.filter((c) => c.closedAt);
    return { active, closed };
  }, [cards.data]);

  const reopen = useMutation({
    mutationFn: (id: string) =>
      api<CardRow>(`/api/cards/${id}/reopen`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cards-admin'] }),
  });
  const reopenBank = useMutation({
    mutationFn: (id: string) =>
      api(`/api/bank-accounts/${id}`, { method: 'PATCH', body: { active: true } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bank-accounts-list'] }),
  });

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<CreditCard className="h-5 w-5" />}
        title="Cards"
        subtitle="Registered company cards. Close or replace when issuers reissue, or after a fraud event."
        meta={
          <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium tabular-nums text-muted-foreground">
            {grouped.active.length} active · {grouped.closed.length} closed
          </span>
        }
        actions={
          <>
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <input
                type="checkbox"
                checked={showClosed}
                onChange={(e) => setShowClosed(e.target.checked)}
              />
              Show closed
            </label>
            {isAdmin ? (
              <Button size="sm" onClick={() => setShowAdd(true)}>
                <Plus className="mr-1.5 h-3.5 w-3.5" />
                Add card
              </Button>
            ) : null}
          </>
        }
      />

      {!isAdmin ? (
        <UICard>
          <CardHeader>
            <CardTitle>Read-only view</CardTitle>
            <CardDescription>
              Only ADMINs can add, close or replace cards. You can still view the registry.
            </CardDescription>
          </CardHeader>
        </UICard>
      ) : null}

      <UICard>
        <CardHeader>
          <CardTitle>Active</CardTitle>
          <CardDescription>
            Cards in use. Each can be closed or replaced. Replacements carry forward the owner, relay-device binding, and FX rates by default.
          </CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead className="bg-muted/30 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-2 text-left">Card</th>
                <th className="px-4 py-2 text-left">Holder</th>
                <th className="px-4 py-2 text-left">Owner (routes OTPs)</th>
                <th className="px-4 py-2 text-right">FX %</th>
                <th className="px-4 py-2 text-right">GST %</th>
                <th className="px-4 py-2 text-left">Credit</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {grouped.active.map((c) => {
                const cc = ccByCardId.get(c.id);
                const limit = c.creditLimit ? parseFloat(c.creditLimit) : 0;
                const used = cc ? parseFloat(cc.used || '0') : 0;
                const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
                return (
                <tr key={c.id} className="border-t">
                  <td className="px-4 py-2">
                    <div className="font-medium">{c.bank} ••{c.lastFour}</div>
                    <div className="text-xs text-muted-foreground">{c.nickname}</div>
                  </td>
                  <td className="px-4 py-2">{c.cardholderName}</td>
                  <td className="px-4 py-2 text-muted-foreground">
                    {c.ownerUserId ? userNameById.get(c.ownerUserId) ?? '—' : '—'}
                  </td>
                  <td className="px-4 py-2 text-right">{c.fxMarkupPct ?? '—'}</td>
                  <td className="px-4 py-2 text-right">{c.fxGstPct ?? '—'}</td>
                  <td className="px-4 py-2 min-w-[180px]">
                    {c.creditLimit ? (
                      <div>
                        <div className="text-xs">
                          <span className="font-medium">₹{used.toLocaleString('en-IN')}</span>
                          <span className="text-muted-foreground"> / ₹{limit.toLocaleString('en-IN')}</span>
                        </div>
                        <div className="mt-1 h-1 w-full rounded-full bg-muted overflow-hidden">
                          <div
                            className={`h-full ${pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        {cc?.daysToDue != null ? (
                          <div className={`mt-1 text-xs ${cc.daysToDue <= 2 ? 'text-red-600 font-medium' : 'text-muted-foreground'}`}>
                            {cc.daysToDue <= 0 ? 'Due today' : cc.daysToDue === 1 ? 'Due tomorrow' : `Due in ${cc.daysToDue}d`}
                          </div>
                        ) : null}
                        {cc?.lastPaymentAt ? (
                          <div className="mt-0.5 text-xs text-muted-foreground">
                            Last paid ₹{parseFloat(cc.lastPaymentAmount ?? '0').toLocaleString('en-IN')} ·{' '}
                            {new Date(cc.lastPaymentAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                          </div>
                        ) : null}
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">Not a CC</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <div className="flex justify-end gap-1.5">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSecretsCard(c)}
                      >
                        <Eye className="mr-1 h-3.5 w-3.5" />
                        {c.hasSecureDetails ? 'Reveal' : 'Details'}
                      </Button>
                      {isAdmin ? (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCcSettingsCard(c)}
                          >
                            <CreditCard className="mr-1 h-3.5 w-3.5" />
                            CC
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setReplacing(c)}
                          >
                            <Repeat className="mr-1 h-3.5 w-3.5" />
                            Replace
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setClosing(c)}
                          >
                            <ShieldOff className="mr-1 h-3.5 w-3.5" />
                            Close
                          </Button>
                        </>
                      ) : null}
                    </div>
                  </td>
                </tr>
              );})}
              {grouped.active.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-6 text-center text-sm text-muted-foreground">
                    No active cards
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </CardContent>
      </UICard>

      {showClosed && grouped.closed.length > 0 ? (
        <UICard>
          <CardHeader>
            <CardTitle>Closed / replaced</CardTitle>
            <CardDescription>
              Historical cards. Old transactions stay attributed here. Replaced cards link to their successor; click "Reopen" if a card was closed by mistake (only available when there is no replacement).
            </CardDescription>
          </CardHeader>
          <CardContent className="overflow-x-auto p-0">
            <table className="w-full text-sm">
              <thead className="bg-muted/30 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-2 text-left">Card</th>
                  <th className="px-4 py-2 text-left">Closed</th>
                  <th className="px-4 py-2 text-left">Reason</th>
                  <th className="px-4 py-2 text-left">Replaced by</th>
                  {isAdmin ? <th className="px-4 py-2 text-right">Actions</th> : null}
                </tr>
              </thead>
              <tbody>
                {grouped.closed.map((c) => {
                  const successor = c.replacedByCardId
                    ? replacementById.get(c.replacedByCardId)
                    : null;
                  return (
                    <tr key={c.id} className="border-t text-muted-foreground">
                      <td className="px-4 py-2">
                        <div className="font-medium text-foreground">
                          {c.bank} ••{c.lastFour}
                        </div>
                        <div className="text-xs">{c.cardholderName}</div>
                      </td>
                      <td className="px-4 py-2 text-xs">
                        {c.closedAt ? new Date(c.closedAt).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-4 py-2">
                        <span className="rounded bg-muted px-1.5 py-0.5 text-xs">
                          {c.closedReason ?? '—'}
                        </span>
                        {c.closedNotes ? (
                          <div className="mt-1 text-xs">{c.closedNotes}</div>
                        ) : null}
                      </td>
                      <td className="px-4 py-2 text-xs">
                        {successor ? (
                          <span className="font-medium text-foreground">
                            {successor.bank} ••{successor.lastFour}
                          </span>
                        ) : c.replacedByCardId ? (
                          <span className="italic">card not in scope</span>
                        ) : (
                          '—'
                        )}
                      </td>
                      {isAdmin ? (
                        <td className="px-4 py-2 text-right">
                          {!c.replacedByCardId ? (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => reopen.mutate(c.id)}
                              disabled={reopen.isPending}
                            >
                              <RotateCw className="mr-1 h-3.5 w-3.5" />
                              Reopen
                            </Button>
                          ) : null}
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </UICard>
      ) : null}

      <UICard>
        <CardHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle>Bank accounts</CardTitle>
              <CardDescription>
                Company current accounts that receive bank-transfer OTPs and debit / credit SMS.
                A bank account can be added or closed — it cannot be replaced.
              </CardDescription>
            </div>
            {isAdmin ? (
              <Button size="sm" className="shrink-0" onClick={() => setShowAddBank(true)}>
                <Plus className="mr-1.5 h-3.5 w-3.5" />
                Add bank account
              </Button>
            ) : null}
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead className="bg-muted/30 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-2 text-left">Account</th>
                <th className="px-4 py-2 text-left">Masked number</th>
                <th className="px-4 py-2 text-left">IFSC</th>
                <th className="px-4 py-2 text-left">Status</th>
                {isAdmin ? <th className="px-4 py-2 text-right">Actions</th> : null}
              </tr>
            </thead>
            <tbody>
              {(bankAccounts.data ?? []).map((b) => (
                <tr key={b.id} className="border-t">
                  <td className="px-4 py-2">
                    <div className={cn('font-medium', !b.active && 'text-muted-foreground')}>{b.bank}</div>
                    <div className="text-xs text-muted-foreground">{b.nickname}</div>
                  </td>
                  <td className="px-4 py-2 tabular-nums">{b.accountNumberMasked ?? '—'}</td>
                  <td className="px-4 py-2 text-muted-foreground">{b.ifsc ?? '—'}</td>
                  <td className="px-4 py-2">
                    {b.active ? (
                      <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700">
                        Active
                      </span>
                    ) : (
                      <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                        Closed
                      </span>
                    )}
                  </td>
                  {isAdmin ? (
                    <td className="px-4 py-2 text-right">
                      {b.active ? (
                        <Button variant="outline" size="sm" onClick={() => setClosingBank(b)}>
                          <ShieldOff className="mr-1 h-3.5 w-3.5" />
                          Close
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => reopenBank.mutate(b.id)}
                          disabled={reopenBank.isPending}
                        >
                          <RotateCw className="mr-1 h-3.5 w-3.5" />
                          Reopen
                        </Button>
                      )}
                    </td>
                  ) : null}
                </tr>
              ))}
              {(bankAccounts.data ?? []).length === 0 ? (
                <tr>
                  <td colSpan={isAdmin ? 5 : 4} className="px-4 py-6 text-center text-sm text-muted-foreground">
                    {bankAccounts.isLoading ? 'Loading…' : 'No bank accounts'}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </CardContent>
      </UICard>

      {showAddBank ? (
        <AddBankAccountDialog
          onClose={() => setShowAddBank(false)}
          onDone={() => {
            setShowAddBank(false);
            qc.invalidateQueries({ queryKey: ['bank-accounts-list'] });
          }}
        />
      ) : null}
      {closingBank ? (
        <CloseBankDialog
          account={closingBank}
          onClose={() => setClosingBank(null)}
          onDone={() => {
            setClosingBank(null);
            qc.invalidateQueries({ queryKey: ['bank-accounts-list'] });
          }}
        />
      ) : null}

      {closing ? (
        <CloseDialog
          card={closing}
          onClose={() => setClosing(null)}
          onDone={() => {
            setClosing(null);
            qc.invalidateQueries({ queryKey: ['cards-admin'] });
          }}
        />
      ) : null}
      {replacing ? (
        <ReplaceDialog
          card={replacing}
          onClose={() => setReplacing(null)}
          onDone={() => {
            setReplacing(null);
            qc.invalidateQueries({ queryKey: ['cards-admin'] });
          }}
        />
      ) : null}
      {showAdd ? (
        <AddCardDialog
          users={users.data ?? []}
          onClose={() => setShowAdd(false)}
          onDone={() => {
            setShowAdd(false);
            qc.invalidateQueries({ queryKey: ['cards-admin'] });
          }}
        />
      ) : null}
      {secretsCard ? (
        <CardSecretsDialog
          card={secretsCard}
          isAdmin={isAdmin}
          onClose={() => setSecretsCard(null)}
          onSaved={() => qc.invalidateQueries({ queryKey: ['cards-admin'] })}
        />
      ) : null}
      {ccSettingsCard ? (
        <CcSettingsDialog
          card={ccSettingsCard}
          onClose={() => setCcSettingsCard(null)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['cards-admin'] });
            qc.invalidateQueries({ queryKey: ['cards', 'credit-summaries'] });
          }}
        />
      ) : null}
    </div>
  );
}

function CcSettingsDialog({
  card,
  onClose,
  onSaved,
}: {
  card: CardRow;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [creditLimit, setCreditLimit] = useState(card.creditLimit ?? '');
  const [dueDay, setDueDay] = useState(card.dueDay ? String(card.dueDay) : '');
  const [beneName, setBeneName] = useState('');
  const [beneAccount, setBeneAccount] = useState('');
  const [beneIfsc, setBeneIfsc] = useState('');
  const [beneUpi, setBeneUpi] = useState('');
  const [error, setError] = useState<string | null>(null);

  const existing = useQuery<{ name: string; account: string | null; ifsc: string | null; upiVpa: string | null }>({
    queryKey: ['cc-beneficiary', card.id],
    queryFn: () => api(`/api/cards/${card.id}/cc-beneficiary`),
    retry: false,
  });
  useEffect(() => {
    if (existing.data) {
      setBeneName(existing.data.name ?? '');
      setBeneAccount(existing.data.account ?? '');
      setBeneIfsc(existing.data.ifsc ?? '');
      setBeneUpi(existing.data.upiVpa ?? '');
    }
  }, [existing.data]);

  const submit = useMutation({
    mutationFn: () =>
      api(`/api/cards/${card.id}/settings`, {
        method: 'POST',
        body: {
          creditLimit: creditLimit ? creditLimit : null,
          dueDay: dueDay ? parseInt(dueDay, 10) : null,
          beneficiary: beneName
            ? {
                name: beneName,
                account: beneAccount || undefined,
                ifsc: beneIfsc || undefined,
                upiVpa: beneUpi || undefined,
              }
            : undefined,
        },
      }),
    onSuccess: () => {
      onSaved();
      onClose();
    },
    onError: (e) => setError((e as ApiError).message || 'Could not save.'),
  });

  const dueDayValid = !dueDay || (parseInt(dueDay, 10) >= 1 && parseInt(dueDay, 10) <= 31);
  const ifscValid = !beneIfsc || /^[A-Z]{4}0[A-Z0-9]{6}$/.test(beneIfsc);
  const ready = dueDayValid && ifscValid && (!creditLimit || /^\d+(\.\d{1,2})?$/.test(creditLimit));

  return (
    <ModalShell
      title={`CC settings — ${card.nickname}`}
      subtitle="Credit limit, due date and the payee this card is paid down to."
      onClose={onClose}
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label htmlFor="cc-limit">Credit limit (INR)</Label>
            <Input
              id="cc-limit"
              inputMode="decimal"
              value={creditLimit}
              onChange={(e) => setCreditLimit(e.target.value)}
              placeholder="e.g. 600000"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="cc-due-day">Due day of month</Label>
            <Input
              id="cc-due-day"
              inputMode="numeric"
              value={dueDay}
              onChange={(e) => setDueDay(e.target.value)}
              placeholder="1-31"
            />
          </div>
        </div>
        <div className="border-t pt-4 space-y-3">
          <div>
            <div className="font-medium text-sm">Payee (for CC bill payment)</div>
            <p className="text-xs text-muted-foreground">
              Saved once; the "Pay a credit card" flow prefills these fields.
            </p>
          </div>
          <div className="space-y-1">
            <Label>Beneficiary name</Label>
            <Input value={beneName} onChange={(e) => setBeneName(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Account number</Label>
              <Input value={beneAccount} onChange={(e) => setBeneAccount(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label>IFSC</Label>
              <Input value={beneIfsc} onChange={(e) => setBeneIfsc(e.target.value.toUpperCase())} placeholder="HDFC0001234" />
            </div>
          </div>
          <div className="space-y-1">
            <Label>UPI VPA (optional)</Label>
            <Input value={beneUpi} onChange={(e) => setBeneUpi(e.target.value)} placeholder="cardpay@bank" />
          </div>
        </div>
        {error ? <p className="text-sm text-destructive">{error}</p> : null}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={!ready || submit.isPending} onClick={() => submit.mutate()}>
            {submit.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Save
          </Button>
        </div>
      </div>
    </ModalShell>
  );
}

function ModalShell({
  title,
  subtitle,
  onClose,
  children,
}: {
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-lg border bg-background shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between border-b px-5 py-3">
          <div>
            <h2 className="text-base font-semibold">{title}</h2>
            {subtitle ? (
              <p className="mt-0.5 text-xs text-muted-foreground">{subtitle}</p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 hover:bg-muted"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function CloseDialog({
  card,
  onClose,
  onDone,
}: {
  card: CardRow;
  onClose: () => void;
  onDone: () => void;
}) {
  const [reason, setReason] = useState(CLOSE_REASONS[0]!.v);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit() {
    setBusy(true);
    setErr(null);
    try {
      await api(`/api/cards/${card.id}/close`, {
        method: 'POST',
        body: { reason, notes: notes.trim() || undefined },
      });
      onDone();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Failed');
      setBusy(false);
    }
  }

  return (
    <ModalShell
      title={`Close ${card.bank} ••${card.lastFour}`}
      subtitle="The card will stop accepting new OTPs and won't appear in pickers. Historical transactions stay attributed to it."
      onClose={onClose}
    >
      <div className="space-y-3 p-5">
        <div className="space-y-1.5">
          <Label>Reason</Label>
          <select
            className="w-full rounded-md border bg-background px-3 py-2 text-sm"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          >
            {CLOSE_REASONS.map((r) => (
              <option key={r.v} value={r.v}>
                {r.label}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="close-notes">Notes (optional)</Label>
          <Input
            id="close-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={500}
            placeholder="e.g. fraud reported on 2026-04-21"
          />
        </div>
        {err ? <p className="text-sm text-destructive">{err}</p> : null}
      </div>
      <div className="flex justify-end gap-2 border-t px-5 py-3">
        <Button variant="outline" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button onClick={submit} disabled={busy}>
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Close card
        </Button>
      </div>
    </ModalShell>
  );
}

function ReplaceDialog({
  card,
  onClose,
  onDone,
}: {
  card: CardRow;
  onClose: () => void;
  onDone: () => void;
}) {
  const [newLastFour, setNewLastFour] = useState('');
  const [newNickname, setNewNickname] = useState('');
  const [copyDeviceBinding, setCopyDeviceBinding] = useState(true);
  const [copyFxRates, setCopyFxRates] = useState(true);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit() {
    if (!/^\d{4}$/.test(newLastFour)) {
      setErr('New last 4 must be exactly 4 digits');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await api(`/api/cards/${card.id}/replace`, {
        method: 'POST',
        body: {
          newLastFour,
          newNickname: newNickname.trim() || undefined,
          copyDeviceBinding,
          copyFxRates,
          notes: notes.trim() || undefined,
        },
      });
      onDone();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Failed');
      setBusy(false);
    }
  }

  return (
    <ModalShell
      title={`Replace ${card.bank} ••${card.lastFour}`}
      subtitle="Closes the old card and creates a new one for the same holder. New card carries forward owner + device binding + FX rates by default."
      onClose={onClose}
    >
      <div className="space-y-3 p-5">
        <div className="space-y-1.5">
          <Label htmlFor="new-last4">New last 4 digits</Label>
          <Input
            id="new-last4"
            value={newLastFour}
            onChange={(e) => setNewLastFour(e.target.value.replace(/\D/g, '').slice(0, 4))}
            maxLength={4}
            placeholder="0601"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="new-nickname">Nickname (optional)</Label>
          <Input
            id="new-nickname"
            value={newNickname}
            onChange={(e) => setNewNickname(e.target.value)}
            placeholder={`${card.bank} ${newLastFour || 'XXXX'}`}
          />
        </div>
        <div className="space-y-1.5">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={copyDeviceBinding}
              onChange={(e) => setCopyDeviceBinding(e.target.checked)}
            />
            Carry forward relay-device binding
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={copyFxRates}
              onChange={(e) => setCopyFxRates(e.target.checked)}
            />
            Carry forward FX markup &amp; GST rates
          </label>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="replace-notes">Notes (optional)</Label>
          <Input
            id="replace-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={500}
            placeholder="e.g. reissued after expiry"
          />
        </div>
        {err ? <p className="text-sm text-destructive">{err}</p> : null}
      </div>
      <div className="flex justify-end gap-2 border-t px-5 py-3">
        <Button variant="outline" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button onClick={submit} disabled={busy || !/^\d{4}$/.test(newLastFour)}>
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Replace card
        </Button>
      </div>
    </ModalShell>
  );
}

function AddCardDialog({
  users,
  onClose,
  onDone,
}: {
  users: UserRow[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [bank, setBank] = useState('');
  const [nickname, setNickname] = useState('');
  const [lastFour, setLastFour] = useState('');
  const [cardholderName, setCardholderName] = useState('');
  const [ownerUserId, setOwnerUserId] = useState('');
  const [fxMarkupPct, setFxMarkupPct] = useState('3.5');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit() {
    if (!/^\d{4}$/.test(lastFour)) {
      setErr('Last 4 must be exactly 4 digits');
      return;
    }
    if (!bank.trim() || !nickname.trim() || !cardholderName.trim()) {
      setErr('Bank, nickname, and cardholder name are required');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await api('/api/cards', {
        method: 'POST',
        body: {
          bank: bank.trim(),
          nickname: nickname.trim(),
          lastFour,
          cardholderName: cardholderName.trim(),
          ownerUserId: ownerUserId || undefined,
          fxMarkupPct: fxMarkupPct || undefined,
          fxGstPct: '18.0',
        },
      });
      onDone();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Failed');
      setBusy(false);
    }
  }

  return (
    <ModalShell title="Add card" onClose={onClose}>
      <div className="grid grid-cols-2 gap-3 p-5">
        <div className="space-y-1.5">
          <Label htmlFor="add-bank">Bank</Label>
          <Input id="add-bank" value={bank} onChange={(e) => setBank(e.target.value)} placeholder="HSBC" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="add-last4">Last 4</Label>
          <Input
            id="add-last4"
            value={lastFour}
            onChange={(e) => setLastFour(e.target.value.replace(/\D/g, '').slice(0, 4))}
            maxLength={4}
          />
        </div>
        <div className="col-span-2 space-y-1.5">
          <Label htmlFor="add-nickname">Nickname</Label>
          <Input id="add-nickname" value={nickname} onChange={(e) => setNickname(e.target.value)} placeholder="HSBC 0601" />
        </div>
        <div className="col-span-2 space-y-1.5">
          <Label htmlFor="add-holder">Cardholder name</Label>
          <Input id="add-holder" value={cardholderName} onChange={(e) => setCardholderName(e.target.value)} placeholder="Aashwin Jain" />
        </div>
        <div className="col-span-2 space-y-1.5">
          <Label htmlFor="add-owner">Owner — admin only (routes OTPs)</Label>
          <select
            id="add-owner"
            className="w-full rounded-md border bg-background px-3 py-2 text-sm"
            value={ownerUserId}
            onChange={(e) => setOwnerUserId(e.target.value)}
          >
            <option value="">— pick an admin —</option>
            {users
              .filter((u) => u.role === 'ADMIN')
              .map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name} · {u.email}
                </option>
              ))}
          </select>
          <p className="text-[11px] text-muted-foreground">
            Card owners are always admins — they receive the card's OTPs.
          </p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="add-fx">FX markup %</Label>
          <Input
            id="add-fx"
            value={fxMarkupPct}
            onChange={(e) => setFxMarkupPct(e.target.value)}
            placeholder="3.5"
          />
        </div>
        <div className="space-y-1.5">
          <Label>GST %</Label>
          <Input value="18.0" disabled />
        </div>
        {err ? <p className="col-span-2 text-sm text-destructive">{err}</p> : null}
      </div>
      <div className="flex justify-end gap-2 border-t px-5 py-3">
        <Button variant="outline" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button onClick={submit} disabled={busy}>
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Add card
        </Button>
      </div>
    </ModalShell>
  );
}

function AddBankAccountDialog({
  onClose,
  onDone,
}: {
  onClose: () => void;
  onDone: () => void;
}) {
  const [bank, setBank] = useState('');
  const [nickname, setNickname] = useState('');
  const [accountNumberMasked, setAccountNumberMasked] = useState('');
  const [ifsc, setIfsc] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit() {
    if (!bank.trim() || !nickname.trim()) {
      setErr('Bank and nickname are required');
      return;
    }
    if (ifsc.trim() && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifsc.trim().toUpperCase())) {
      setErr('IFSC looks invalid — expected format like HDFC0001234');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await api('/api/bank-accounts', {
        method: 'POST',
        body: {
          bank: bank.trim(),
          nickname: nickname.trim(),
          accountNumberMasked: accountNumberMasked.trim() || undefined,
          ifsc: ifsc.trim() ? ifsc.trim().toUpperCase() : undefined,
        },
      });
      onDone();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Failed');
      setBusy(false);
    }
  }

  return (
    <ModalShell
      title="Add bank account"
      subtitle="Register a company current account so its bank-transfer OTPs and debit / credit SMS are captured."
      onClose={onClose}
    >
      <div className="space-y-3 p-5">
        <div className="space-y-1.5">
          <Label htmlFor="bank-name">Bank</Label>
          <Input id="bank-name" value={bank} onChange={(e) => setBank(e.target.value)} placeholder="HDFC" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="bank-nickname">Nickname</Label>
          <Input
            id="bank-nickname"
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            placeholder="HDFC current"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="bank-masked">Account number (masked)</Label>
          <Input
            id="bank-masked"
            value={accountNumberMasked}
            onChange={(e) => setAccountNumberMasked(e.target.value)}
            placeholder="XXXXXXXXXX7207"
          />
          <p className="text-[11px] text-muted-foreground">
            Enter it as the bank SMS shows it — the trailing digits are how debit / credit SMS get
            matched to this account.
          </p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="bank-ifsc">IFSC (optional)</Label>
          <Input
            id="bank-ifsc"
            value={ifsc}
            onChange={(e) => setIfsc(e.target.value.toUpperCase())}
            maxLength={11}
            placeholder="HDFC0001234"
          />
        </div>
        {err ? <p className="text-sm text-destructive">{err}</p> : null}
      </div>
      <div className="flex justify-end gap-2 border-t px-5 py-3">
        <Button variant="outline" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button onClick={submit} disabled={busy}>
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Add bank account
        </Button>
      </div>
    </ModalShell>
  );
}

function CloseBankDialog({
  account,
  onClose,
  onDone,
}: {
  account: BankAccountRow;
  onClose: () => void;
  onDone: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit() {
    setBusy(true);
    setErr(null);
    try {
      await api(`/api/bank-accounts/${account.id}`, {
        method: 'PATCH',
        body: { active: false },
      });
      onDone();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Failed');
      setBusy(false);
    }
  }

  return (
    <ModalShell
      title={`Close ${account.bank} account`}
      subtitle="The account stops receiving OTPs and won't appear in pickers. Historical transactions stay attributed to it — you can reopen it later."
      onClose={onClose}
    >
      <div className="space-y-3 p-5 text-sm">
        <p>
          Close <span className="font-medium">{account.bank}</span>
          {account.accountNumberMasked ? ` · ${account.accountNumberMasked}` : ''} ({account.nickname})?
        </p>
        {err ? <p className="text-destructive">{err}</p> : null}
      </div>
      <div className="flex justify-end gap-2 border-t px-5 py-3">
        <Button variant="outline" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button onClick={submit} disabled={busy}>
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Close account
        </Button>
      </div>
    </ModalShell>
  );
}

function CardSecretsDialog({
  card,
  isAdmin,
  onClose,
  onSaved,
}: {
  card: CardRow;
  isAdmin: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [revealed, setRevealed] = useState<{
    cardNumber: string | null;
    cardExpiry: string | null;
    cardCvv: string | null;
  } | null>(null);
  const [revealing, setRevealing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState(!card.hasSecureDetails);
  const [number, setNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvv, setCvv] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function reveal() {
    setRevealing(true);
    setErr(null);
    try {
      const r = await api<{
        cardNumber: string | null;
        cardExpiry: string | null;
        cardCvv: string | null;
      }>(`/api/cards/${card.id}/reveal`, { method: 'POST' });
      setRevealed(r);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Reveal failed');
    }
    setRevealing(false);
  }

  function copyNumber() {
    if (!revealed?.cardNumber) return;
    void navigator.clipboard.writeText(revealed.cardNumber).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  async function save() {
    const num = number.replace(/\s/g, '');
    if (num && !/^\d{12,19}$/.test(num)) {
      setErr('Card number must be 12–19 digits');
      return;
    }
    if (expiry && !/^(0[1-9]|1[0-2])\/\d{2}$/.test(expiry)) {
      setErr('Expiry must be MM/YY');
      return;
    }
    if (cvv && !/^\d{3,4}$/.test(cvv)) {
      setErr('CVV must be 3–4 digits');
      return;
    }
    if (!num && !expiry && !cvv) {
      setErr('Enter at least one field');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await api(`/api/cards/${card.id}/secure-details`, {
        method: 'PATCH',
        body: {
          cardNumber: num || undefined,
          cardExpiry: expiry || undefined,
          cardCvv: cvv || undefined,
        },
      });
      onSaved();
      onClose();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Save failed');
      setBusy(false);
    }
  }

  return (
    <ModalShell
      title={`${card.bank} ••${card.lastFour} — card details`}
      subtitle="Encrypted at rest. Every reveal is recorded in the audit log."
      onClose={onClose}
    >
      <div className="space-y-4 p-5">
        {card.hasSecureDetails ? (
          revealed ? (
            <div className="space-y-3 rounded-lg border bg-muted/30 p-3">
              <div>
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  Card number
                </div>
                <div className="mt-0.5 flex items-center gap-2">
                  <span className="font-mono text-lg tabular-nums">
                    {revealed.cardNumber ?? '—'}
                  </span>
                  {revealed.cardNumber ? (
                    <Button size="sm" variant="outline" onClick={copyNumber}>
                      {copied ? (
                        <Check className="mr-1 h-3.5 w-3.5 text-success" />
                      ) : (
                        <Copy className="mr-1 h-3.5 w-3.5" />
                      )}
                      {copied ? 'Copied' : 'Copy'}
                    </Button>
                  ) : null}
                </div>
              </div>
              <div className="flex gap-8">
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    Expiry
                  </div>
                  <div className="mt-0.5 font-mono text-lg">{revealed.cardExpiry ?? '—'}</div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    CVV
                  </div>
                  <div className="mt-0.5 font-mono text-lg">{revealed.cardCvv ?? '—'}</div>
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground">
                Expiry &amp; CVV are shown to type in — only the card number is copyable.
              </p>
            </div>
          ) : (
            <Button onClick={reveal} disabled={revealing}>
              {revealing ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Eye className="mr-2 h-4 w-4" />
              )}
              Reveal card details
            </Button>
          )
        ) : (
          <p className="text-sm text-muted-foreground">No card details saved for this card yet.</p>
        )}

        {isAdmin ? (
          editing ? (
            <div className="space-y-3 border-t pt-4">
              <p className="text-xs font-medium text-muted-foreground">
                {card.hasSecureDetails ? 'Update details' : 'Add details'}
              </p>
              <div className="space-y-1.5">
                <Label htmlFor="cs-num">Card number</Label>
                <Input
                  id="cs-num"
                  value={number}
                  onChange={(e) => setNumber(e.target.value)}
                  inputMode="numeric"
                  placeholder="16-digit card number"
                  autoComplete="off"
                />
              </div>
              <div className="flex gap-3">
                <div className="flex-1 space-y-1.5">
                  <Label htmlFor="cs-exp">Expiry (MM/YY)</Label>
                  <Input
                    id="cs-exp"
                    value={expiry}
                    onChange={(e) => setExpiry(e.target.value)}
                    placeholder="08/29"
                    maxLength={5}
                    autoComplete="off"
                  />
                </div>
                <div className="flex-1 space-y-1.5">
                  <Label htmlFor="cs-cvv">CVV</Label>
                  <Input
                    id="cs-cvv"
                    value={cvv}
                    onChange={(e) => setCvv(e.target.value)}
                    inputMode="numeric"
                    maxLength={4}
                    autoComplete="off"
                  />
                </div>
              </div>
            </div>
          ) : (
            <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
              Update card details
            </Button>
          )
        ) : null}

        {err ? <p className="text-sm text-destructive">{err}</p> : null}
      </div>
      <div className="flex justify-end gap-2 border-t px-5 py-3">
        <Button variant="outline" onClick={onClose} disabled={busy}>
          Close
        </Button>
        {isAdmin && editing ? (
          <Button onClick={save} disabled={busy}>
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Save details
          </Button>
        ) : null}
      </div>
    </ModalShell>
  );
}
